/* eslint-disable consistent-return */
import React from 'react';
import PropTypes from 'prop-types';
import { safeRest } from '@wds/utils';
import { useTheme } from 'react-jss';
import { PROGRESS_CIRCLE_SIZES, PROGRESS_CIRCLE_COLORS } from '@wds/helpers';

import useStyles from './useStyles';

const CIRCLE_CIRCUMFERENCE = 339.292;

/*
 * @todo need a title / aria-label attached to progress bar
 * https://dequeuniversity.com/rules/axe/4.1/aria-progressbar-name?application=axeAPI
 */

const ProgressCircle = ({
  children,
  size,
  percent,
  color,
  continuous,
  valueMax,
  valueMin,
  valueText,
  ...rest
}) => {
  const theme = useTheme();
  const classes = useStyles({ color, size, continuous, theme });

  const circleOptions = {
    lg: {
      strokeWidth: 8,
      radius: 56,
    },
    sm: {
      strokeWidth: 16,
      radius: 52,
    },
    xs: {
      strokeWidth: 20,
      radius: 50,
    },
  };

  const { strokeWidth, radius } = circleOptions[size] || {
    strokeWidth: 12,
    radius: 54,
  };

  const valueNow = percent ? parseFloat(percent) * 100 : undefined;
  const strokeDasharray = !continuous
    ? `${CIRCLE_CIRCUMFERENCE} ${CIRCLE_CIRCUMFERENCE}`
    : undefined;
  const strokeDashoffset = !continuous
    ? CIRCLE_CIRCUMFERENCE * (1 - percent)
    : undefined;

  return (
    <div
      className={classes.wrenchProgressCircle}
      role="progressbar"
      aria-valuenow={valueNow}
      aria-valuemax={valueMax}
      aria-valuemin={valueMin}
      aria-valuetext={valueText}
      {...safeRest(rest)}
    >
      <svg
        className={classes.wrenchCirclePath}
        role="img"
        aria-label="Progress"
        viewBox="0 0 120 120"
      >
        <g className={classes.wrenchCirclePathGroup}>
          <circle
            className={classes.wrenchCirclePathBorder}
            cx="60"
            cy="60"
            r={radius}
            strokeWidth={strokeWidth}
          />
          <circle
            className={classes.wrenchCirclePathFill}
            cx="60"
            cy="60"
            r={radius}
            strokeWidth={strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
          />
        </g>
      </svg>
      {children && (
        <div className={classes.wrenchCircleDisplay}>{children}</div>
      )}
    </div>
  );
};

ProgressCircle.propTypes = {
  /**
   * The progress can wrap around content such as text, numbers, icons
   */
  children: PropTypes.node,
  /**
   * Percentage of current progress
   */
  percent: PropTypes.number,
  /**
   * Size variants
   */
  size: PropTypes.oneOf(PROGRESS_CIRCLE_SIZES),
  /**
   * Color variants
   */
  color: PropTypes.oneOf(PROGRESS_CIRCLE_COLORS),
  /**
   * Continuous percent for indeterminate progress
   */
  continuous: PropTypes.bool,
  /**
   * Maximum value number
   */
  valueMax: (props, propName) => {
    if (props[propName] && typeof props[propName] !== 'number') {
      return new Error(
        `Invalid prop type ${propName} supplied. Should be type number`
      );
    }
    if (props.percent && typeof props[propName] === 'undefined') {
      return new Error(`If the prop percent is used, ${propName} is required`);
    }
  },
  /**
   * Minimum value number
   */
  valueMin: (props, propName) => {
    if (props[propName] && typeof props[propName] !== 'number') {
      return new Error(
        `Invalid prop type ${propName} supplied. Should be type number`
      );
    }
    if (props.percent && typeof props[propName] === 'undefined') {
      return new Error(`If the prop percent is used, ${propName} is required`);
    }
  },
  /**
   * Aria value text - https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_progressbar_role
   */
  valueText: (props, propName) => {
    if (props[propName] && typeof props[propName] !== 'string') {
      return new Error(
        `Invalid prop type ${propName} supplied should be type string`
      );
    }
    if (props.continuous && typeof props[propName] === 'undefined') {
      return new Error(
        `If the value of the progress bar is indeterminate, ${propName} is required`
      );
    }
  },
};

ProgressCircle.defaultProps = {
  children: undefined,
  percent: undefined,
  color: 'success',
  size: 'md',
  continuous: false,
  valueMax: undefined,
  valueMin: undefined,
  valueText: undefined,
};

export default ProgressCircle;
